import { useData } from '../useData';
import { useBudgetPreferences } from './useBudgetPreferences';

import { TABLES } from '../../constants';

export const useBudgetData = projectId => {
  // Reference data - always fetch regardless of projectId
  const { data: costTypes } = useData(TABLES.REFERENCE_COST_TYPES, {
    select: 'id,name',
    realtime: false,
  });

  const { data: paymentScheduleTypes } = useData(TABLES.REFERENCE_PAYMENT_SCHEDULE_TYPES, {
    select: 'id,name',
    realtime: false,
  });

  const { data: itemTypes } = useData(TABLES.REFERENCE_ITEM_TYPES, {
    select: 'id,name,display_order',
    realtime: false,
  });

  // Shared budget preferences (persisted to localStorage, synced across components)
  const {
    includeMonthlyPayments,
    setIncludeMonthlyPayments,
    includeEstimatedCosts,
    setIncludeEstimatedCosts,
  } = useBudgetPreferences();

  // Get project info first
  const {
    data: projectInfo,
    isLoading: isLoadingProjectInfo,
    error: projectInfoError,
    update: mutateProjectInfo,
  } = useData(TABLES.PROJECT_INFO, {
    enabled: Boolean(projectId),
    filter: { id: projectId },
    realtime: true,
  });

  // Get master items next
  const {
    data: masterItems,
    isLoading: isLoadingMasterItems,
    error: masterItemsError,
  } = useData(TABLES.MASTER_ITEMS, {
    select: 'id,item_name,item_type_id,cost_type_id,estimated_cost,bundle_items,bb_chapter_id',
    realtime: false,
    enabled: Boolean(projectId),
  });

  // Then get project items (now includes override_cost_type_id, moved from project_financials)
  const {
    data: projectItems,
    isLoading: isLoadingItems,
    error: itemsError,
    update: mutateProjectItem,
  } = useData(TABLES.PROJECT_ITEMS, {
    select: `id, master_item_id, total_cost, status, item_type_id, incl_vat, active_quote_line_id, override_cost_type_id, relationships, vendor:project_vendors(master_vendor_id, master_vendors(id, name))`,
    filter: { project_id: projectId },
    realtime: true,
    enabled: Boolean(projectId),
  });

  // Phase 10: Fetch BB chapters with budget_rollup flag for budget breakdown grouping
  const {
    data: bbChapters,
    isLoading: isLoadingBbChapters,
    error: bbChaptersError,
  } = useData(TABLES.REFERENCE_BB_CHAPTERS, {
    select: 'id,code,name_he,friendly_name,depth,parent_id,budget_rollup,display_order',
    realtime: false,
    enabled: Boolean(projectId),
  });

  // Fetch selected vendor quotes for budget totals (replaces project_financials)
  const {
    data: vendorQuotes,
    isLoading: isLoadingQuotes,
    error: quotesError,
  } = useData(TABLES.VENDOR_QUOTES, {
    select: 'id,total_price,status,master_vendor_id,incl_vat,vat_rate',
    filter: { project_id: projectId },
    realtime: true,
    enabled: Boolean(projectId),
  });

  // Handle loading and error states
  const isLoading =
    isLoadingProjectInfo ||
    isLoadingMasterItems ||
    isLoadingItems ||
    isLoadingBbChapters ||
    isLoadingQuotes;

  const error =
    projectInfoError || masterItemsError || itemsError || bbChaptersError || quotesError;

  // Data update functions
  const updateProjectItem = async (itemId, updates) => {
    await mutateProjectItem({
      id: itemId,
      data: updates,
    });
  };

  const updateProjectInfo = async updates => {
    return await mutateProjectInfo({
      id: projectId,
      data: updates,
    });
  };

  return {
    // Reference data - always available
    referenceData: {
      costTypes,
      paymentScheduleTypes,
      itemTypes,
    },
    // Project data - only available when projectId is provided
    data: {
      projectInfo,
      masterItems,
      projectItems,
      bbChapters,
      vendorQuotes,
      costTypes,
      paymentScheduleTypes,
      itemTypes,
    },

    // State management
    includeMonthlyPayments,
    setIncludeMonthlyPayments,
    includeEstimatedCosts,
    setIncludeEstimatedCosts,
    // Data updates
    updateProjectItem,
    updateProjectInfo,
    // Status
    isLoading,
    error,
  };
};
